import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveBroadcast } from '@/hooks/use-workspaces';
import { Megaphone, AlertTriangle, AlertOctagon, Info, X, ExternalLink } from '@/components/icons';

const DISMISS_KEY_PREFIX = 'koinon_dismissed_broadcast_';

function renderFormattedText(text: string) {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*.*?\*\*|\[.*?\]\(https?:\/\/[^\s)]+\))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      parts.push(<strong key={match.index}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('[') && token.includes('](')) {
      const closingBracket = token.indexOf('](');
      const label = token.slice(1, closingBracket);
      const url = token.slice(closingBracket + 2, -1);
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80 font-semibold inline-flex items-center gap-0.5"
        >
          {label}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

export const BroadcastBanner: React.FC = () => {
  const { t } = useTranslation();
  const { data: broadcast } = useActiveBroadcast();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!broadcast) {
      setDismissed(false);
      return;
    }
    try {
      const stored = localStorage.getItem(`${DISMISS_KEY_PREFIX}${broadcast.id}`);
      if (!stored) {
        setDismissed(false);
        return;
      }
      if (broadcast.updated_at && new Date(broadcast.updated_at).getTime() > new Date(stored).getTime()) {
        setDismissed(false);
        return;
      }
      setDismissed(true);
    } catch {
      setDismissed(false);
    }
  }, [broadcast, broadcast?.id, broadcast?.updated_at]);

  if (!broadcast || !broadcast.is_active || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    try {
      localStorage.setItem(`${DISMISS_KEY_PREFIX}${broadcast.id}`, new Date().toISOString());
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const levelStyles = {
    info: 'bg-blue-900/80 border-blue-700/60 text-blue-100',
    warning: 'bg-amber-950/85 border-amber-700/60 text-amber-100',
    emergency: 'bg-rose-950/95 border-rose-600/80 text-rose-50 animate-pulse',
  };

  const icons = {
    info: <Info className="w-4 h-4 text-blue-300 shrink-0" />,
    warning: <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />,
    emergency: <AlertOctagon className="w-4 h-4 text-rose-300 shrink-0" />,
  };

  return (
    <aside
      aria-label={t('common.platform_announcement')}
      className={`border-b px-4 py-2 text-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 sm:gap-3 transition-colors ${
        levelStyles[broadcast.level] || levelStyles.info
      }`}
    >
      <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
        <span className="mt-0.5 sm:mt-0 shrink-0">
          {icons[broadcast.level] || <Megaphone className="w-4 h-4 text-primary shrink-0" />}
        </span>
        <span className="font-bold uppercase tracking-wider text-[9px] px-1.5 py-0.5 rounded bg-black/35 shrink-0">
          {broadcast.level}
        </span>
        <div className="min-w-0 text-left leading-relaxed">
          <strong className="mr-1.5 font-bold">{broadcast.title}:</strong>
          <span className="opacity-95">{renderFormattedText(broadcast.message)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
        {broadcast.action_url && (
          <a
            href={broadcast.action_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-white/15 hover:bg-white/25 text-white transition-all shrink-0 border border-white/20"
          >
            <span>{broadcast.action_label || t('common.learn_more')}</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white/15 rounded-md transition-colors text-white/80 hover:text-white"
          title={t('common.dismiss_announcement')}
          aria-label={t('common.dismiss_announcement')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
};
